import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCreateBlogPost } from '@/hooks/use-blog';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { AdminHeader } from '@/components/admin-header';
import { PostTitleSection } from '@/components/post-title-section';
import { RichTextEditorSection } from '@/components/rich-text-editor-section';
import { ExcerptSection } from '@/components/excerpt-section';
import { SEOSection } from '@/components/seo-section';
import { PublishSettingsSection } from '@/components/publish-settings-section';
import { CategoriesTagsSection } from '@/components/categories-tags-section';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useUploadMedia } from '@/hooks/use-media';
import { MediaPicker } from '@/components/media-picker';
import { Upload, X, FolderOpen, Sparkles, Loader2 } from 'lucide-react';
import * as React from 'react';
import { getApiClient } from '@/lib/api-client';

export const Route = createFileRoute('/blog/new')({
  component: CreateBlogPage,
});

type BlogStatus = 'draft' | 'published' | 'archived';
type BlogType = 'article' | 'video';

function CreateBlogPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const createBlogPost = useCreateBlogPost();
  const uploadMedia = useUploadMedia();

  // Form state
  const [title, setTitle] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [content, setContent] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [excerpt, setExcerpt] = React.useState('');
  const [status, setStatus] = React.useState<BlogStatus>('draft');
  const [type, setType] = React.useState<BlogType>('article');
  const [tags, setTags] = React.useState<string[]>([]);
  const [category, setCategory] = React.useState('');
  const [thumbnailUrl, setThumbnailUrl] = React.useState('');
  const [youtubeUrl, setYoutubeUrl] = React.useState('');
  const [seoTitle, setSeoTitle] = React.useState('');
  const [seoDescription, setSeoDescription] = React.useState('');

  // UI state
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);
  const [thumbnailPickerOpen, setThumbnailPickerOpen] = React.useState(false);
  const [isUploadingThumbnail, setIsUploadingThumbnail] = React.useState(false);
  
  // AI Import state
  const [importUrl, setImportUrl] = React.useState('');
  const [isImporting, setIsImporting] = React.useState(false);
  const [importError, setImportError] = React.useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = React.useState(false);
  const [importProgress, setImportProgress] = React.useState<{
    step: string;
    status: 'pending' | 'loading' | 'completed' | 'error';
    duration?: number; // Duration in seconds
  }[]>([]);
  const [progressTimers, setProgressTimers] = React.useState<Map<number, NodeJS.Timeout>>(new Map());
  const [stepStartTimes, setStepStartTimes] = React.useState<Map<number, number>>(new Map());
  const [importedData, setImportedData] = React.useState<{
    title: string;
    description: string;
    content: string;
    seo_title: string;
    seo_keywords: string[];
    tags: string[];
  } | null>(null);

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      // Cleanup all timers when component unmounts
      if (progressTimers.size > 0) {
        progressTimers.forEach(timer => clearInterval(timer));
      }
    };
  }, []); // Empty dependency array - only cleanup on unmount

  // Helper function to detect YouTube video in content or youtubeUrl
  const hasYouTubeVideo = React.useCallback((contentText: string, youtubeUrlText: string): boolean => {
    // Check youtubeUrl field
    if (youtubeUrlText.trim()) {
      const youtubeUrlPattern = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
      if (youtubeUrlPattern.test(youtubeUrlText)) {
        return true;
      }
    }

    // Check content for YouTube embeds or links
    if (contentText.trim()) {
      const youtubePatterns = [
        /youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)([^"&?\/\s]{11})/i,
        /youtu\.be\/([^"&?\/\s]{11})/i,
        /youtube\.com\/embed\/([^"&?\/\s]{11})/i,
        /youtube\.com\/v\/([^"&?\/\s]{11})/i,
      ];
      
      for (const pattern of youtubePatterns) {
        if (pattern.test(contentText)) {
          return true;
        }
      }
    }

    return false;
  }, []);

  // Auto-detect type based on YouTube video presence
  React.useEffect(() => {
    const hasVideo = hasYouTubeVideo(content, youtubeUrl);
    setType(hasVideo ? 'video' : 'article');
  }, [content, youtubeUrl, hasYouTubeVideo]);

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSave = async (targetStatus?: 'draft' | 'published') => {
    // Validation
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = 'Tiêu đề là bắt buộc';
    }
    if (!content.trim()) {
      newErrors.content = 'Nội dung là bắt buộc';
    }
    if (content.trim().length < 10) {
      newErrors.content = 'Nội dung phải có ít nhất 10 ký tự';
    }
    if (!user?.id) {
      newErrors.submit = 'Bạn cần đăng nhập để tạo bài viết';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSaveMessage(null);

    try {
      // If targetStatus is provided, use it; otherwise keep current status
      const finalStatus = targetStatus || status;

      // Auto-detect type based on YouTube video presence
      const detectedType = hasYouTubeVideo(content.trim(), youtubeUrl.trim()) ? 'video' : 'article';

      // Generate slug from title if not provided
      const finalSlug = slug.trim() || (title
        ? title
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
        : undefined);

      const payload = {
        title: title.trim(),
        slug: finalSlug || undefined,
        content: content.trim(),
        description: description.trim() || undefined,
        excerpt: excerpt.trim() || undefined,
        status: finalStatus === 'archived' ? 'draft' : finalStatus,
        type: detectedType,
        tags: tags.length > 0 ? tags : undefined,
        category: category.trim() || undefined,
        thumbnail_url: thumbnailUrl || undefined,
        youtube_url: detectedType === 'video' ? youtubeUrl.trim() || undefined : undefined,
        seo_title: seoTitle.trim() || undefined,
        seo_description: seoDescription.trim() || undefined,
        author_id: user!.id,
      };

      const newPost = await createBlogPost.mutateAsync(payload);

      // Update local state from response to ensure consistency
      if (newPost) {
        if (newPost.status) {
          setStatus(newPost.status as BlogStatus);
        }
        if (newPost.slug) {
          setSlug(newPost.slug);
        }
      }

      setSaveMessage('✓ Đã tạo bài viết thành công!');
      setTimeout(() => {
        setSaveMessage(null);
        // Navigate to edit page after creation
        navigate({ to: '/blog/$id', params: { id: newPost.id } });
      }, 2000);
    } catch (error) {
      const err = error as Error;
      setErrors({ submit: err.message || 'Không thể tạo bài viết' });
    }
  };

  const handleSaveOnly = async () => {
    // Save without changing status - keep current status
    await handleSave();
  };

  const handleSaveAndPublish = async () => {
    await handleSave('published');
  };

  const handleOpenImportModal = () => {
    setImportModalOpen(true);
    setImportUrl('');
    setImportError(null);
    setImportedData(null);
    setImportProgress([]);
    // Clear any existing timers
    progressTimers.forEach(timer => clearInterval(timer));
    setProgressTimers(new Map());
    setStepStartTimes(new Map());
  };

  // Helper function to start timer for a step
  const startStepTimer = (stepIndex: number) => {
    const startTime = Date.now();
    setStepStartTimes(prev => new Map(prev).set(stepIndex, startTime));

    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setImportProgress(prev => prev.map((p, i) => 
        i === stepIndex && p.status === 'loading' 
          ? { ...p, duration: elapsed }
          : p
      ));
    }, 1000); // Update every second

    setProgressTimers(prev => new Map(prev).set(stepIndex, timer));
  };

  // Helper function to stop timer for a step
  const stopStepTimer = (stepIndex: number) => {
    const timer = progressTimers.get(stepIndex);
    if (timer) {
      clearInterval(timer);
      setProgressTimers(prev => {
        const newMap = new Map(prev);
        newMap.delete(stepIndex);
        return newMap;
      });
    }

    const startTime = stepStartTimes.get(stepIndex);
    if (startTime) {
      const finalDuration = Math.floor((Date.now() - startTime) / 1000);
      setImportProgress(prev => prev.map((p, i) => 
        i === stepIndex 
          ? { ...p, duration: finalDuration }
          : p
      ));
    }
  };

  const handleImportFromUrl = async () => {
    if (!importUrl.trim()) {
      setImportError('Vui lòng nhập URL');
      return;
    }

    // Validate URL
    try {
      new URL(importUrl.trim());
    } catch {
      setImportError('URL không hợp lệ. Vui lòng nhập URL đầy đủ (ví dụ: https://example.com)');
      return;
    }

    setIsImporting(true);
    setImportError(null);
    setImportedData(null);
    
    // Initialize progress steps
    const steps = [
      { step: 'Đang phân tích URL...', status: 'loading' as const, duration: 0 },
      { step: 'Đang gọi Perplexica API để tìm kiếm thông tin...', status: 'pending' as const },
      { step: 'Đang tổng hợp và phân tích nội dung từ Perplexica...', status: 'pending' as const },
      { step: 'Đang tạo bài viết...', status: 'pending' as const },
    ];
    setImportProgress(steps);

    try {
      // Step 1: Analyzing URL
      startStepTimer(0);
      await new Promise(resolve => setTimeout(resolve, 300));
      stopStepTimer(0);
      setImportProgress(prev => prev.map((p, i) => i === 0 ? { ...p, status: 'completed' } : i === 1 ? { ...p, status: 'loading' } : p));

      // Step 2: Calling Perplexica API (this is the actual API call)
      startStepTimer(1);
      const apiClient = getApiClient();
      const response = await apiClient.request<{
        success: boolean;
        data: {
          title: string;
          description: string;
          content: string;
          seo_title: string;
          seo_keywords: string[];
          tags: string[];
        };
        error?: string;
        meta?: {
          perplexicaCallDuration?: number;
        };
      }>('/blog/posts/import-from-url', {
        method: 'POST',
        body: JSON.stringify({ url: importUrl.trim() }),
      });
      stopStepTimer(1);

      // Step 2 completed, move to step 3
      setImportProgress(prev => prev.map((p, i) => i === 1 ? { ...p, status: 'completed' } : i === 2 ? { ...p, status: 'loading' } : p));

      // Step 3: Processing content (simulate processing time)
      startStepTimer(2);
      await new Promise(resolve => setTimeout(resolve, 500));
      stopStepTimer(2);
      setImportProgress(prev => prev.map((p, i) => i === 2 ? { ...p, status: 'completed' } : i === 3 ? { ...p, status: 'loading' } : p));

      // Step 4: Complete
      startStepTimer(3);
      await new Promise(resolve => setTimeout(resolve, 300));
      stopStepTimer(3);
      setImportProgress(prev => prev.map((p, i) => i === 3 ? { ...p, status: 'completed' } : p));

      if (response.error) {
        setImportError(response.error);
        // Stop all timers on error
        progressTimers.forEach(timer => clearInterval(timer));
        setProgressTimers(new Map());
        setImportProgress(prev => prev.map(p => ({ ...p, status: 'error' })));
        return;
      }

      if (response.success && response.data) {
        setImportedData(response.data);
      }
    } catch (error) {
      console.error('Import error:', error);
      // Stop all timers on error
      progressTimers.forEach(timer => clearInterval(timer));
      setProgressTimers(new Map());
      setImportError(
        error instanceof Error 
          ? error.message 
          : 'Không thể import bài viết từ URL này. Vui lòng thử lại sau.'
      );
      setImportProgress(prev => prev.map(p => ({ ...p, status: 'error' })));
    } finally {
      setIsImporting(false);
    }
  };

  const handleConfirmImport = () => {
    if (!importedData) return;

    // Fill form with imported data
    setTitle(importedData.title || '');
    setDescription(importedData.description || '');
    setContent(importedData.content || '');
    setSeoTitle(importedData.seo_title || importedData.title || '');
    setSeoDescription(importedData.description || '');
    setTags(importedData.tags || []);
    
    // Auto-generate slug from title
    if (importedData.title) {
      const generatedSlug = importedData.title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generatedSlug);
    }

    setSaveMessage('✓ Đã import bài viết từ URL thành công!');
    setTimeout(() => {
      setSaveMessage(null);
    }, 3000);

    // Close modal
    setImportModalOpen(false);
    setImportedData(null);
    setImportUrl('');
    setImportProgress([]);
    // Clear all timers
    progressTimers.forEach(timer => clearInterval(timer));
    setProgressTimers(new Map());
    setStepStartTimes(new Map());
  };

  const handleThumbnailUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          setIsUploadingThumbnail(true);
          const result = await uploadMedia.mutateAsync({
            file,
            folder: 'blog/thumbnails',
          });
          if (result?.media?.url) {
            setThumbnailUrl(result.media.url);
          }
        } catch (error) {
          console.error('Thumbnail upload error:', error);
          setErrors({ thumbnail: 'Không thể tải lên thumbnail' });
        } finally {
          setIsUploadingThumbnail(false);
        }
      }
    };
    input.click();
  };

  // Generate permalink: use slug if exists, otherwise use generated slug from title
  const permalinkSlug = slug.trim() || (title
    ? title
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    : '');
  const postUrl = `/blog/${permalinkSlug}`;
  const permalink = postUrl;

  // Map blog status to agent moderation status for PublishSettingsSection
  const mapStatusToModerationStatus = (status: BlogStatus): 'draft' | 'pending' | 'published' | 'unpublished' | 'rejected' => {
    switch (status) {
      case 'draft':
        return 'draft';
      case 'published':
        return 'published';
      case 'archived':
        return 'unpublished';
      default:
        return 'draft';
    }
  };

  const mapModerationStatusToBlogStatus = (modStatus: 'draft' | 'pending' | 'published' | 'unpublished' | 'rejected'): BlogStatus => {
    switch (modStatus) {
      case 'draft':
      case 'pending':
        return 'draft';
      case 'published':
        return 'published';
      case 'unpublished':
      case 'rejected':
        return 'archived';
      default:
        return 'draft';
    }
  };

  if (!user) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600 font-medium">
            Bạn cần đăng nhập để tạo bài viết
          </p>
        </div>
        <Link to="/blog">
          <Button variant="outline">Quay lại danh sách</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <AdminHeader
        breadcrumbs={[
          { label: 'Blog', href: '/blog' },
          { label: 'Tạo Bài viết Mới' },
        ]}
        title="Tạo Bài viết Mới"
        onPreview={() => window.open(postUrl, '_blank')}
        onSave={handleSaveOnly}
        onSaveAndPublish={handleSaveAndPublish}
        onWriteByAI={handleOpenImportModal}
        isSaving={createBlogPost.isPending}
        isPublished={false}
      />

      {/* Error Banner */}
      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md mx-6 mt-6">
          <p className="text-sm text-red-600 font-medium">{errors.submit}</p>
        </div>
      )}

      {/* Success Message */}
      {saveMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md mx-6 mt-6">
          <p className="text-sm text-green-700 font-medium">{saveMessage}</p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto bg-gray-50 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Post Title Section */}
            <PostTitleSection
              value={title}
              onChange={(value) => {
                setTitle(value);
                clearError('title');
                // Auto-generate slug from title if slug is empty
                if (!slug.trim()) {
                  const generatedSlug = value
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');
                  setSlug(generatedSlug);
                }
              }}
              permalink={permalink}
              code={slug}
              onCodeChange={setSlug}
              onCodeSave={() => {
                // For new posts, slug will be saved when creating the post
                // This is just for display purposes
              }}
              isCodeSaving={false}
              error={errors.title}
              required
              prefix="/blog/"
              label="Tiêu đề bài viết"
              placeholder="Nhập tiêu đề bài viết..."
            />

            {/* Rich Text Editor Section */}
            <RichTextEditorSection
              value={content}
              onChange={(value) => {
                setContent(value);
                clearError('content');
              }}
              placeholder="Bắt đầu viết nội dung bài viết..."
              label="Nội dung"
              error={errors.content}
              required
            />

            {/* Description Section (for video posts) */}
            {type === 'video' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mô tả video
                  </label>
                  <Textarea
                    value={description}
                    onChange={(e) => {
                      setDescription(e.target.value);
                      clearError('description');
                    }}
                    placeholder="Nhập mô tả cho video..."
                    rows={4}
                    className={`resize-none ${
                      errors.description ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.description && (
                    <p className="text-sm text-red-500 mt-1">{errors.description}</p>
                  )}
                </div>
              </div>
            )}

            {/* Excerpt Section */}
            <ExcerptSection
              value={excerpt}
              onChange={setExcerpt}
              placeholder="Viết một đoạn tóm tắt ngắn về bài viết..."
              helperText="Tùy chọn. Đoạn này sẽ được sử dụng trong preview và meta description."
            />

            {/* SEO Section */}
            <SEOSection
              metaTitle={seoTitle}
              metaDescription={seoDescription}
              onMetaTitleChange={setSeoTitle}
              onMetaDescriptionChange={setSeoDescription}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Publish Settings */}
            <PublishSettingsSection
              status={mapStatusToModerationStatus(status)}
              visibility="public"
              onStatusChange={(modStatus) => {
                setStatus(mapModerationStatusToBlogStatus(modStatus));
              }}
              onVisibilityChange={() => {
                // Blog posts are always public
              }}
            />

            {/* Featured Image */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Hình ảnh đại diện</h3>
              </div>
              <div className="p-6">
                {thumbnailUrl ? (
                  <div className="relative">
                    <img
                      src={thumbnailUrl}
                      alt="Thumbnail"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200 bg-gray-50"
                    />
                    <button
                      onClick={() => setThumbnailUrl('')}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                      title="Xóa hình ảnh"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={handleThumbnailUpload}
                      disabled={isUploadingThumbnail}
                      className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploadingThumbnail ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                          <p className="text-sm font-medium text-gray-600">
                            Đang tải lên...
                          </p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mx-auto text-gray-400 mb-4" />
                          <p className="text-sm font-medium text-gray-600 mb-2">
                            Tải lên hình ảnh đại diện
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, GIF tối đa 50MB
                          </p>
                        </>
                      )}
                    </button>
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setThumbnailPickerOpen(true)}
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Chọn từ Media Library
                    </Button>
                  </div>
                )}
                {errors.thumbnail && (
                  <p className="text-sm text-red-500 mt-2">{errors.thumbnail}</p>
                )}
              </div>
            </div>

            {/* YouTube URL */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">YouTube URL</h3>
              </div>
              <div className="p-6">
                <Input
                  value={youtubeUrl}
                  onChange={(e) => {
                    setYoutubeUrl(e.target.value);
                    clearError('youtube_url');
                  }}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className={errors.youtube_url ? 'border-red-500' : ''}
                />
                {errors.youtube_url && (
                  <p className="text-sm text-red-500 mt-1">{errors.youtube_url}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Thêm YouTube URL hoặc embed YouTube vào nội dung để tự động đánh dấu bài viết có video
                </p>
              </div>
            </div>

            {/* Categories & Tags */}
            <CategoriesTagsSection
              categories={category ? [category] : []}
              tags={tags}
              onCategoriesChange={(categories) => {
                setCategory(categories[0] || '');
              }}
              onTagsChange={setTags}
            />
          </div>
        </div>
      </div>

      {/* Media Picker Modal */}
      <MediaPicker
        isOpen={thumbnailPickerOpen}
        onClose={() => setThumbnailPickerOpen(false)}
        onSelect={(url) => {
          setThumbnailUrl(url);
          setThumbnailPickerOpen(false);
        }}
        fileType="image"
      />

      {/* AI Import Modal */}
      {importModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Tạo Blog bằng AI từ URL</h3>
              </div>
              <button
                onClick={() => {
                  setImportModalOpen(false);
                  setImportUrl('');
                  setImportError(null);
                  setImportedData(null);
                  setImportProgress([]);
                  // Clear all timers
                  progressTimers.forEach(timer => clearInterval(timer));
                  setProgressTimers(new Map());
                  setStepStartTimes(new Map());
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {!importedData ? (
                <>
                  {/* URL Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nhập URL bài viết
                    </label>
                    <div className="flex gap-3">
                      <Input
                        type="url"
                        value={importUrl}
                        onChange={(e) => {
                          setImportUrl(e.target.value);
                          setImportError(null);
                        }}
                        placeholder="https://example.com/article"
                        className={importError ? 'border-red-500' : ''}
                        disabled={isImporting}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !isImporting && importUrl.trim()) {
                            handleImportFromUrl();
                          }
                        }}
                      />
                      <Button
                        onClick={handleImportFromUrl}
                        disabled={isImporting || !importUrl.trim()}
                        className="px-6"
                      >
                        {isImporting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Đang xử lý...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Phân tích
                          </>
                        )}
                      </Button>
                    </div>
                    {importError && (
                      <p className="text-sm text-red-500 mt-2">{importError}</p>
                    )}
                  </div>

                  {/* Progress Steps */}
                  {isImporting && importProgress.length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700">Tiến trình xử lý:</h4>
                      {importProgress.map((progress, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            {progress.status === 'completed' ? (
                              <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            ) : progress.status === 'loading' ? (
                              <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
                            ) : progress.status === 'error' ? (
                              <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                                <X className="w-4 h-4 text-white" />
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                          <div className="flex-1 flex items-center justify-between">
                            <span className={`text-sm ${
                              progress.status === 'completed' ? 'text-green-600' :
                              progress.status === 'loading' ? 'text-purple-600 font-medium' :
                              progress.status === 'error' ? 'text-red-600' :
                              'text-gray-500'
                            }`}>
                              {progress.step}
                            </span>
                            {progress.duration !== undefined && progress.duration > 0 && (
                              <span className={`text-xs font-mono ${
                                progress.status === 'completed' ? 'text-green-600' :
                                progress.status === 'loading' ? 'text-purple-600' :
                                progress.status === 'error' ? 'text-red-600' :
                                'text-gray-500'
                              }`}>
                                {progress.duration}s
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Preview Content */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-gray-900">Preview nội dung đã tạo:</h4>
                    
                    {/* Title Preview */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Tiêu đề</label>
                      <h3 className="text-xl font-bold text-gray-900">{importedData.title}</h3>
                    </div>

                    {/* Description Preview */}
                    {importedData.description && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Mô tả</label>
                        <p className="text-sm text-gray-700">{importedData.description}</p>
                      </div>
                    )}

                    {/* Content Preview */}
                    <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-auto">
                      <label className="block text-xs font-medium text-gray-500 mb-2">Nội dung</label>
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ 
                          __html: importedData.content
                            .replace(/\n/g, '<br>')
                            .replace(/### (.*?)(<br>|$)/g, '<h3>$1</h3>')
                            .replace(/## (.*?)(<br>|$)/g, '<h2>$1</h2>')
                            .replace(/# (.*?)(<br>|$)/g, '<h1>$1</h1>')
                            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                            .replace(/\*(.*?)\*/g, '<em>$1</em>')
                            .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" class="text-blue-600 hover:underline">$1</a>')
                        }}
                      />
                    </div>

                    {/* Tags Preview */}
                    {importedData.tags && importedData.tags.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="block text-xs font-medium text-gray-500 mb-2">Tags</label>
                        <div className="flex flex-wrap gap-2">
                          {importedData.tags.map((tag, index) => (
                            <span key={index} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setImportModalOpen(false);
                  setImportUrl('');
                  setImportError(null);
                  setImportedData(null);
                  setImportProgress([]);
                  // Clear all timers
                  progressTimers.forEach(timer => clearInterval(timer));
                  setProgressTimers(new Map());
                  setStepStartTimes(new Map());
                }}
              >
                {importedData ? 'Hủy' : 'Đóng'}
              </Button>
              {importedData && (
                <Button
                  onClick={handleConfirmImport}
                  className="px-6"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Import vào Form
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
